import { stringHash } from "@src/util/common/string-hash";
import { EventListDataType, getStepByMode } from "./manage-state-event-lists";

const MAX_RATE_COMPARE_COLOR = 0.2;
export const MAX_COLOR_INDEX = 6;

export type DataMode = "compareFirstStep" | "comparePreviousStep" | "compareWithUserGroup";
export interface ResultDataType {
  user_count: number;
  rate: number;
  originRate: number;
  exp_group: string;
  step: string;
}

export class MappingFunnelDataSource {
  private eventList: EventListDataType[] = [];
  private variants: string[] = [];
  private breakdown: string | null = null;
  private breakdownValues: string[] = [];
  private rawData: any[] = [];
  private isLoading = false;

  private negativeRateAfterCompare: { min: number; max: number } | null = null;
  private positiveRateAfterCompare: { min: number; max: number } | null = null;

  constructor({
    eventLists,
    variants,
    breakdown,
    breakdownValues,
  }: {
    eventLists: EventListDataType[];
    variants: string[];
    breakdown: string | null;
    breakdownValues: string[];
  }) {
    this.eventList = eventLists;
    this.variants = variants;
    this.breakdown = breakdown;
    this.breakdownValues = breakdownValues;
  }

  public setRawData(rawData: any[]) {
    this.rawData = rawData;
  }
  public setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  public getHashKey() {
    return stringHash(
      JSON.stringify({
        eventLists: this.eventList,
        variants: this.variants,
        breakdown: this.breakdown,
        breakdownValues: this.breakdownValues,
      }),
    );
  }
  private formatData(data: Record<string, any>, step: string, isFirstStep: boolean, mode: DataMode): ResultDataType {
    return {
      user_count: data["user_count"] ?? 0,
      rate: data[step] ?? 0,
      originRate: this.getOriginRateFirstStepByMode(isFirstStep, mode, data[step] ?? 0),
      exp_group: data["exp_group"],
      step: step,
    };
  }

  private groupByStep(data: any[], step: string) {
    return Object.groupBy(data, () => step);
  }

  private resetInstant() {
    this.negativeRateAfterCompare = null;
    this.positiveRateAfterCompare = null;
  }
  // private traceMinMaxRate(newData: ResultDataType) {
  //   if (!newData) return newData;
  //   if (newData.rate < 0) {
  //     if (this.negativeRateAfterCompare === null) {
  //       this.negativeRateAfterCompare = {
  //         min: newData.rate,
  //         max: newData.rate,
  //       };
  //     } else {
  //       if (Math.abs(newData.rate) < Math.abs(this.negativeRateAfterCompare.min))
  //         this.negativeRateAfterCompare.min = newData.rate;
  //       if (Math.abs(newData.rate) > Math.abs(this.negativeRateAfterCompare.max))
  //         this.negativeRateAfterCompare.max = newData.rate;
  //     }
  //   }
  //   if (newData.rate > 0) {
  //     if (this.positiveRateAfterCompare === null) {
  //       this.positiveRateAfterCompare = {
  //         min: newData.rate,
  //         max: newData.rate,
  //       };
  //     } else {
  //       if (newData.rate < this.positiveRateAfterCompare.min) this.positiveRateAfterCompare.min = newData.rate;
  //       if (newData.rate > this.positiveRateAfterCompare.max) this.positiveRateAfterCompare.max = newData.rate;
  //     }
  //   }

  //   return newData;
  // }
  // private getNewStepInfoToKeep(current: ResultDataType | null, newData: ResultDataType, mode: DataMode, index: number) {
  //   if (mode === "compareFirstStep") {
  //     if (index === 0) return newData;
  //     return current;
  //   }
  //   return newData;
  // }

  // private getRateByMode(data: ResultDataType, mode: DataMode, stepKeepInfo: ResultDataType | null) {
  //   return {
  //     ...data,
  //     rate: stepKeepInfo === null ? data.rate : data.rate - stepKeepInfo.rate,
  //   };
  // }

  private getOriginRateFirstStepByMode(isFirstStep: boolean, mode: DataMode, originRate: number) {
    if (!isFirstStep) return originRate;
    if (mode === "compareFirstStep") return 1;
    if (mode === "comparePreviousStep") return -1;
    return originRate;
  }

  private executeWithBreakdown(mode: DataMode, breakdownBy: string) {
    // const keepBeforeStep: Record<string, ResultDataType | null> | null = {};
    return this.eventList
      .sort((a, b) => a.order - b.order)
      .map((event, index) => {
        const renameStep = getStepByMode(
          event.name,
          mode,
          this.eventList.map((_event) => _event.name),
        );

        const groupRawDataByStep = this.groupByStep(this.rawData, renameStep)[renameStep] ?? [];

        const result = this.breakdownValues.reduce((acc, _breakdownValue) => {
          const groupByBreakdownValue =
            Object.groupBy(groupRawDataByStep, () => _breakdownValue)[_breakdownValue] ?? [];

          acc[_breakdownValue] = this.variants.reduce((acc, currentVariant) => {
            // const keepDataKey = _breakdownValue + currentVariant;
            const formatData = this.formatData(
              Object.groupBy(groupByBreakdownValue, () => currentVariant)[currentVariant]?.find(
                (data) => data[breakdownBy] === _breakdownValue && data["exp_group"] === currentVariant,
              ) ?? {},
              renameStep,
              index === 0,
              mode,
            );
            // const dataWithCompareRate = this.getRateByMode(formatData, mode, keepBeforeStep[keepDataKey] ?? null);
            acc[currentVariant] = formatData;
            //trace min max
            // this.traceMinMaxRate(dataWithCompareRate);
            // keepBeforeStep[keepDataKey] = this.getNewStepInfoToKeep(
            //   keepBeforeStep[keepDataKey],
            //   dataWithCompareRate,
            //   mode,
            //   index,
            // );

            return acc;
          }, {} as Record<string, ResultDataType>);

          return acc;
        }, {} as Record<string, Record<string, ResultDataType>>);
        return {
          step: event.name,
          index: index,
          ...result,
        };
      });
  }

  private executeWithNoneBreakdown(mode: DataMode) {
    // const keepBeforeStep: Record<string, ResultDataType | null> | null = {};
    return this.eventList
      .sort((a, b) => a.order - b.order)
      .map((event, index) => {
        const renameStep = getStepByMode(
          event.name,
          mode,
          this.eventList.map((_event) => _event.name),
        );
        const groupRawDataByStep = this.groupByStep(this.rawData, renameStep)[renameStep] ?? [];

        const result = this.variants.reduce((acc, currentVariant) => {
          const formatData = this.formatData(
            Object.groupBy(groupRawDataByStep, () => currentVariant)[currentVariant]?.find(
              (data) => data["exp_group"] === currentVariant,
            ) ?? {},
            renameStep,
            index === 0,
            mode,
          );
          // const dataWithCompareRate = this.getRateByMode(formatData, mode, keepBeforeStep[currentVariant] ?? null);
          acc[currentVariant] = formatData;
          //trace min max
          // this.traceMinMaxRate(dataWithCompareRate);
          // keepBeforeStep[currentVariant] = this.getNewStepInfoToKeep(
          //   keepBeforeStep[currentVariant],
          //   dataWithCompareRate,
          //   mode,
          //   index,
          // );

          return acc;
        }, {} as Record<string, ResultDataType>);

        return {
          step: event.name,
          index: index,
          ...result,
        };
      });
  }
  //get colors
  private getIndexInRange(min: number, max: number, compareRate: number) {
    const clamped = Math.max(min, Math.min(compareRate, max));
    const percent = (clamped - min) / (max - min);
    const index = Math.min(MAX_COLOR_INDEX - 1, Math.floor(percent * 10));
    return index;
  }

  public getColorIndex(compareRate: number): number {
    if (Math.abs(compareRate) >= MAX_RATE_COMPARE_COLOR) {
      return MAX_COLOR_INDEX;
    }
    if (compareRate < 0) {
      return this.negativeRateAfterCompare === null
        ? 0
        : this.getIndexInRange(
            Math.abs(this.negativeRateAfterCompare?.min),
            Math.abs(this.negativeRateAfterCompare?.max),
            Math.abs(compareRate),
          );
    }
    if (compareRate > 0) {
      return this.positiveRateAfterCompare === null
        ? 0
        : this.getIndexInRange(this.positiveRateAfterCompare?.min, this.positiveRateAfterCompare?.max, compareRate);
    }
    return 0;
  }
  public generateDataSource(mode: DataMode) {
    this.resetInstant();
    if (this.isLoading) return [];
    const _breakdownBy = this.breakdown;
    if (_breakdownBy) {
      return this.executeWithBreakdown(mode, _breakdownBy);
    }

    return this.executeWithNoneBreakdown(mode);
  }
}
